import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/dashboard.css'; // Reusing existing dashboard styles
import Header from '../component/header'; // Added Header import

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SchemeWiseSurvey = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    
    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Scheme Wise Survey",
            subtitle: "Overview of survey progress by government scheme.",
            searchPlaceholder: "Search Scheme...",
            loading: "Loading Data...",
            noRecordsFound: "No records found matching",
            unknown: "Unknown Scheme",
            totalSurvey: "Total Survey :",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            cantSay: "Can't Say"
        },
        hi: {
            title: "योजना वार सर्वेक्षण",
            subtitle: "सरकारी योजना द्वारा सर्वेक्षण प्रगति का अवलोकन।",
            searchPlaceholder: "योजना खोजें...",
            loading: "डेटा लोड हो रहा है...",
            noRecordsFound: "कोई रिकॉर्ड नहीं मिला",
            unknown: "अज्ञात योजना",
            totalSurvey: "कुल सर्वेक्षण :",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            cantSay: "नहीं कह सकते"
        },
        gu: {
            title: "યોજના વાઈઝ સર્વે",
            subtitle: "સરકારી યોજના દ્વારા સર્વે પ્રગતિની ઝાંખી.",
            searchPlaceholder: "યોજના શોધો...",
            loading: "ડેટા લોડ થઈ રહ્યો છે...",
            noRecordsFound: "કોઈ રેકોર્ડ મળ્યો નથી",
            unknown: "અજ્ઞાત યોજના",
            totalSurvey: "કુલ સર્વે :",
            positive: "હકારાત્મક",
            negative: "નકારાત્મક",
            doubtful: "શંકાસ્પદ",
            cantSay: "કહી શકતા નથી"
        }
    };

    const txt = t[lang];
    
    const [schemes, setSchemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [totalSurveyed, setTotalSurveyed] = useState(0);

    useEffect(() => {
        if (appId) {
            fetchData();
        }
    }, [appId]);

    const fetchData = async () => {
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_scheme_wise_survey_dash`, {
                app_id: appId
            });

            if (response.data.success) {
                const result = response.data.data.result || [];
                setSchemes(result);
                
                // Calculate total surveyed across all schemes for the footer
                const total = result.reduce((sum, item) => sum + (item.total || 0), 0);
                setTotalSurveyed(total);
            }
        } catch (error) {
            console.error("Error fetching scheme wise survey:", error);
        } finally {
            setLoading(false);
        }
    };

    // Filter Logic
    const filteredSchemes = schemes.filter(scheme => {
        const name = scheme.scheme_name || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <div className="app-container dashboard-page">
            <div className='dashboard-content container-fluid p-4'>
                
                {/* --- Header Component Integrated Here --- */}
                <Header 
                    title={txt.title} 
                />

                {/* --- Search Bar Row --- */}
                <div className="row fade-in-up mt-3" style={{ animationDelay: '0.1s' }}>
                    <div className="col-12">
                        <div className="d-flex justify-content-end align-items-center flex-wrap gap-3">
                            <div className="search-box-wrapper">
                                <svg className="search-icon" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input 
                                    type="text" 
                                    className="header-search-input" 
                                    placeholder={txt.searchPlaceholder}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- Content List --- */}
                <div className="container-fluid mt-4" style={{ flex: 1 }}>
                    {loading ? (
                        <div className="text-center p-5 text-muted">{txt.loading}</div>
                    ) : (
                        <div className="row g-4">
                            {filteredSchemes.length > 0 ? (
                                filteredSchemes.map((scheme, index) => (
                                    <div key={scheme.scheme_id || index} className="col-12 col-md-6 col-xl-4 fade-in-up">
                                        
                                        {/* --- NAVIGATION ADDED HERE --- */}
                                        <div 
                                            className="scheme-survey-card h-100" 
                                            onClick={() => navigate('/dis_scheme_wise_survey_voter', { state: { scheme_id: scheme.scheme_id } })}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            
                                            {/* Header: Circle Badge, Name, Counts */}
                                            <div className="scheme-card-header">
                                                <div className="header-left">
                                                    <div className="index-badge">
                                                        {index + 1}
                                                    </div>
                                                    <div className="scheme-info">
                                                        <h3 className="scheme-name">
                                                            {scheme.scheme_name || txt.unknown}
                                                        </h3>
                                                    </div>
                                                </div>
                                                <div className="header-right">
                                                    <span className="survey-label-text">{txt.totalSurvey}</span> 
                                                    <span className="survey-val-text">{scheme.total || 0}</span>
                                                </div>
                                            </div>

                                            {/* Stats Grid */}
                                            <div className="scheme-stats-grid">
                                                <div className="s-stat-box bg-green-light">
                                                    <span className="s-stat-label">{txt.positive}</span>
                                                    <span className="s-stat-val text-green">{scheme.P || 0}</span>
                                                </div>
                                                <div className="s-stat-box bg-red-light">
                                                    <span className="s-stat-label">{txt.negative}</span>
                                                    <span className="s-stat-val text-red">{scheme.N || 0}</span>
                                                </div>
                                                <div className="s-stat-box bg-orange-light">
                                                    <span className="s-stat-label">{txt.doubtful}</span>
                                                    <span className="s-stat-val text-orange">{scheme.D || 0}</span>
                                                </div>
                                                <div className="s-stat-box bg-blue-light">
                                                    <span className="s-stat-label">{txt.cantSay}</span>
                                                    <span className="s-stat-val text-blue">{scheme.C || 0}</span>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12 text-center text-muted p-4">
                                    {txt.noRecordsFound} "{searchTerm}"
                                </div>
                            )}
                        </div>
                    )}
                </div>

                
            </div>

            <style jsx>{`
                /* Search Bar Styling */
                .search-box-wrapper {
                    position: relative;
                    width: 100%;
                    max-width: 300px;
                }
                .header-search-input {
                    width: 100%;
                    padding: 10px 10px 10px 40px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    outline: none;
                    font-size: 14px;
                    background-color: white;
                    color: #1e293b;
                    transition: border-color 0.2s;
                }
                .header-search-input:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                .search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                }

                /* Card Styles */
                .scheme-survey-card {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    border: 1px solid #f1f5f9;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.2s ease;
                    border-left: 4px solid #1e3a8a; /* Blue stripe on left */
                }
                .scheme-survey-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08);
                }

                /* Card Header */
                .scheme-card-header {
                    padding: 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-bottom: 1px solid #f8fafc;
                }
                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                }
                .index-badge {
                    width: 40px;
                    height: 40px;
                    background-color: #1e3a8a; /* Dark Blue */
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 16px;
                    flex-shrink: 0;
                }
                .scheme-info {
                    display: flex;
                    flex-direction: column;
                }
                .scheme-name {
                    font-size: 16px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }
                .header-right {
                    text-align: right;
                }
                .survey-label-text {
                    display: block;
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 600;
                    margin-bottom: 2px;
                }
                .survey-val-text {
                    font-size: 18px;
                    font-weight: 800;
                    color: #1e293b;
                }

                /* Stats Grid */
                .scheme-stats-grid {
                    padding: 20px;
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px;
                }
                .s-stat-box {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 12px 5px;
                    border-radius: 8px;
                    text-align: center;
                }
                .s-stat-label {
                    font-size: 11px;
                    font-weight: 700;
                    margin-bottom: 4px;
                    color: #334155;
                }
                .s-stat-val {
                    font-size: 16px;
                    font-weight: 800;
                }

                /* Background Colors */
                .bg-green-light { background-color: #f0fdf4; }
                .bg-red-light { background-color: #fef2f2; }
                .bg-orange-light { background-color: #fff7ed; }
                .bg-blue-light { background-color: #eff6ff; }

                /* Text Colors */
                .text-green { color: #166534; }
                .text-red { color: #991b1b; }
                .text-orange { color: #9a3412; }
                .text-blue { color: #1e40af; }

                /* Page Footer */
                .footer-bar {
                    background-color: white;
                    padding: 15px 25px;
                    border-top: 1px solid #e2e8f0;
                    position: sticky;
                    bottom: 0;
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    box-shadow: 0 -4px 6px -1px rgba(0,0,0,0.02);
                    z-index: 50;
                }
                .footer-text {
                    font-size: 14px;
                    color: #1e3a8a;
                    background: #fff;
                    padding: 8px 18px;
                    border-radius: 8px;
                    border: 1px solid #1e3a8a;
                    font-weight: 700;
                }

                /* =========================================
                   RESPONSIVE MEDIA QUERIES (MOBILE FIXES)
                   ========================================= */
                @media (max-width: 768px) {
                    .search-box-wrapper {
                        max-width: 100%;
                        margin-top: 8px;
                    }
                    .page-header {
                        align-items: flex-start !important;
                        flex-direction: column;
                    }
                    .header-content {
                        margin-bottom: 8px;
                    }
                    .scheme-card-header {
                        padding: 16px;
                    }
                    .scheme-stats-grid {
                        padding: 16px;
                        gap: 8px;
                    }
                    .footer-bar {
                        justify-content: center;
                        padding: 12px 15px;
                    }
                }

                @media (max-width: 480px) {
                    .index-badge {
                        width: 34px;
                        height: 34px;
                        font-size: 14px;
                    }
                    .scheme-name {
                        font-size: 14px;
                    }
                    .survey-val-text {
                        font-size: 16px;
                    }
                    .survey-label-text {
                        font-size: 11px;
                    }
                    .s-stat-box {
                        padding: 8px 4px;
                    }
                    .s-stat-val {
                        font-size: 14px;
                    }
                    .s-stat-label {
                        font-size: 9px;
                        letter-spacing: -0.5px; /* Prevents columns from overflowing on small screens */
                    }
                    .footer-text {
                        font-size: 12px;
                        padding: 6px 14px;
                    }
                }
            `}</style>
        </div>
    );
};

export default SchemeWiseSurvey;